import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Typography for authored HTML — lesson blocks, overview items, and the editor that
 * produces them.
 *
 * Two jobs, and it is worth keeping them distinct:
 *
 * 1. **De-duplication.** Three surfaces render the same authored HTML and each carried
 *    its own near-copy of these rules. They had already drifted.
 * 2. **Surviving Preflight.** Tailwind's reset strips things this content relies on —
 *    `list-style: none` on lists, `font-size/font-weight: inherit` on headings, and
 *    `margin: 0` on everything. Rules that were previously unnecessary because the
 *    browser supplied them now have to be written down.
 *
 * Everything here reproduces what the browser renders today. The one deliberate
 * exception is recorded on the heading rules below.
 *
 * **Always apply this nested under the element that wraps the authored HTML**, never
 * spread directly onto a container:
 *
 *   sx={{ p: 2, [`& .${AUTHORED_CONTENT_CLASS}`]: authoredContentSx }}   // read
 *   sx={{ '& .ProseMirror': authoredContentSx }}                        // editor
 *
 * The read surfaces render `<Box><div dangerouslySetInnerHTML /></Box>`, so a direct
 * spread would make `& > :first-child` match that wrapper `div` rather than the first
 * authored block, and the margin-trim rules would silently do nothing.
 */

/** Put this on the element that receives authored HTML. */
export const AUTHORED_CONTENT_CLASS = 'authored-content';

export const authoredContentSx = {
  // Every vertical margin below is written out, including ones that look redundant.
  // Preflight's `*` reset zeroes margin on everything, and MUI's CssBaseline only
  // resets `body` — so any side these surfaces previously left to the browser is a
  // side that silently collapses to zero. `margin-top` here is the clearest case: the
  // old rule set only `mb`, and because adjacent paragraphs collapse margins, the
  // 1em top was what actually produced the gap between them. Dropping it would have
  // tightened paragraph spacing across every lesson block in the product.
  '& p': { mt: '1em', mb: 1 },

  // Preflight sets `font-size: inherit; font-weight: inherit` on h1–h6, so sizes and
  // weights must be explicit or headings flatten into body text.
  //
  // Deliberate change: LessonBlockEditor previously used tighter heading margins than
  // the read surfaces (mt:1/mb:0.5 against mt:2/mb:1), so lesson content visibly
  // shifted when a user clicked into edit mode. Unifying on the read values closes
  // that jump. This is the only place the contract changes rendering.
  '& h1': { fontSize: '1.75rem', fontWeight: 600, mt: 2, mb: 1 },
  '& h2': { fontSize: '1.4rem', fontWeight: 600, mt: 2, mb: 1 },
  '& h3': { fontSize: '1.15rem', fontWeight: 600, mt: 1.5, mb: 0.75 },
  // h4–h6 previously carried margins only and took their size and weight from the
  // browser. Those are the UA values, written down.
  '& h4': { fontSize: '1em', fontWeight: 'bold', mt: 1, mb: 1 },
  '& h5': { fontSize: '0.83em', fontWeight: 'bold', mt: 1, mb: 1 },
  '& h6': { fontSize: '0.67em', fontWeight: 'bold', mt: 1, mb: 1 },

  // Preflight sets `list-style: none`. Without these, every authored bullet and
  // numbered list loses its marker — the indent alone survives.
  //
  // The nesting selectors mirror the browser's own stylesheet exactly: `circle` at
  // depth two applies to a `ul` inside *either* list type, `square` at depth three
  // likewise, and `ol` is `decimal` at every depth — browsers do not switch nested
  // ordered lists to letters or roman numerals, so neither do we.
  '& ul': { listStyleType: 'disc', pl: 3, my: '1em' },
  '& ol': { listStyleType: 'decimal', pl: 3, my: '1em' },
  '& ul ul, & ol ul': { listStyleType: 'circle' },
  '& ol ol ul, & ol ul ul, & ul ol ul, & ul ul ul': { listStyleType: 'square' },
  // Browsers give a nested list no vertical margin, so a sub-list sits tight against
  // its parent item rather than opening a gap. All four nesting combinations.
  '& ul ul, & ul ol, & ol ul, & ol ol': { my: 0 },
  '& li': { mb: 0.25 },

  '& blockquote': {
    borderLeft: '3px solid',
    borderColor: 'divider',
    pl: 2,
    // The UA default is `margin: 1em 40px`. The old rule overrode only the left side,
    // leaving the other three to the browser — so all three need writing down.
    my: '1em',
    mr: '40px',
    ml: 0,
    fontStyle: 'italic',
    color: 'text.secondary',
  },

  '& code': {
    bgcolor: 'grey.100',
    borderRadius: 0.5,
    px: 0.5,
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  '& pre': {
    bgcolor: 'grey.900',
    color: 'grey.100',
    borderRadius: 1,
    p: 2,
    // The old rule set padding only; the UA `margin: 1em 0` was doing the spacing.
    my: '1em',
    overflow: 'auto',
    '& code': { bgcolor: 'transparent', color: 'inherit', px: 0 },
  },

  '& img': {
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    my: 1,
    borderRadius: 1,
  },

  '& a': { color: 'primary.main', textDecoration: 'underline' },

  // No surface styled `hr` before, so it rendered as the UA inset bevel. Preflight
  // zeroes the margin and leaves a 1px currentColor line. Restoring the spacing and
  // tinting the line to the divider token is the one visual liberty taken here: the
  // UA bevel is a 2px ridge that no current browser renders attractively, and
  // matching it exactly is not worth a bespoke border-style.
  '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: '0.5em', mx: 'auto' },

  // Preflight switches tables to `border-collapse: collapse`. These restore the UA
  // defaults rather than introducing borders — authored tables here are pasted, and
  // giving them a house style belongs to the identity follow-up, not to a change
  // whose contract is to leave rendering alone.
  '& table': { borderCollapse: 'separate', borderSpacing: '2px' },
  // Preflight resets `padding` alongside `margin`. Cell padding is the only UA padding
  // this content relied on that no rule here replaces — 1px, so barely visible, but
  // the same class of omission as the margins above.
  '& th, & td': { padding: '1px' },

  // Stop the first and last child's margins escaping the container, which would
  // otherwise read as inconsistent padding between blocks. `:first-child`, not
  // `:first-of-type` — the latter matches the first element of *every* type, so it
  // would strip the top margin from several blocks instead of one.
  '& > :first-child': { mt: 0 },
  '& > :last-child': { mb: 0 },
} satisfies SxProps<Theme>;

/**
 * Browser defaults, written down, for surfaces that must **not** take the full
 * contract — the document canvas (11pt Google-Docs scale) and the rubric editor
 * (0.875rem). Applying `authoredContentSx` to either would restyle them.
 *
 * Sizes are `em`-relative so they stay correct at any base font size.
 */
export const uaRestore = {
  heading3: {
    '& h3': { fontSize: '1.17em', fontWeight: 'bold', margin: '1em 0' },
  },
  headings4to6: {
    '& h4': { fontSize: '1em', fontWeight: 'bold', margin: '1.33em 0' },
    '& h5': { fontSize: '0.83em', fontWeight: 'bold', margin: '1.67em 0' },
    '& h6': { fontSize: '0.67em', fontWeight: 'bold', margin: '2.33em 0' },
  },
  /**
   * Markers only. For surfaces that already declare their own list padding and
   * margins and would be restyled by having the UA box values imposed on them.
   */
  listMarkers: {
    '& ul': { listStyleType: 'disc' },
    '& ol': { listStyleType: 'decimal' },
    '& ul ul, & ol ul': { listStyleType: 'circle' },
    '& ol ol ul, & ol ul ul, & ul ol ul, & ul ul ul': { listStyleType: 'square' },
  },
  /** Markers plus the UA box model, for surfaces with no list rules of their own. */
  lists: {
    '& ul': { listStyleType: 'disc', paddingLeft: '40px', margin: '1em 0' },
    '& ol': { listStyleType: 'decimal', paddingLeft: '40px', margin: '1em 0' },
    '& ul ul, & ol ul': { listStyleType: 'circle' },
    '& ol ol ul, & ol ul ul, & ul ol ul, & ul ul ul': { listStyleType: 'square' },
    // Separate from the marker selectors above, which cover only the combinations
    // where the *inner* list is a `ul`. Nested lists of either type take no vertical
    // margin in the browser.
    '& ul ul, & ul ol, & ol ul, & ol ol': { margin: 0 },
  },
  // `LinkText` is the CSS system colour for an unvisited link, which is what these
  // surfaces render today. Using the brand colour instead would be a restyle.
  link: {
    '& a': { color: 'LinkText', textDecoration: 'underline' },
  },
  rule: {
    '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', margin: '0.5em auto' },
  },
} satisfies Record<string, SxProps<Theme>>;

/** Elements the contract governs. Used by the structural test. */
export const AUTHORED_CONTENT_ELEMENTS = [
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'img',
  'a',
  'hr',
  'table',
] as const;
